import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Building2,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  DoorOpen,
  Flame,
  Presentation,
  FileText,
  FolderOpen,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  Pencil,
  Unlock,
  Percent,
  Coins,
  Tag,
  Gavel,
  Save,
  ShieldAlert,
  Megaphone,
  Package,
  PhoneCall,
  Smartphone,
  Sparkles,
  Receipt,
  Vote,
  Trash2,
  Upload,
  Users,
  Wrench,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, type Condominio } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";
import { BLOCOS_VAZIO, BlocosCadastro, type BlocosForm } from "./BlocosCadastro";
import { ChamadaExtraModal } from "./ChamadaExtraModal";
import { FracaoIdealModal } from "./FracaoIdealModal";
import { VisualizadorPaginas } from "./VisualizadorPaginas";

// Métodos de rateio de água (espelha METODOS_AGUA do backend; fallback para o
// dropdown sempre mostrar as opções, mesmo se o endpoint não retornar).
const OPCOES_AGUA: Record<string, string> = {
  fracao_ideal: "Fração ideal",
  moradores: "Nº de moradores (per capita)",
  partes_iguais: "Partes iguais",
  individual: "Medição individual (não se aplica)",
};

interface Props {
  condominioId: number;
  onVoltar: () => void;
  onVerResultados: (competenciaId: number) => void;
  onAbrirBiblioteca?: () => void;
  onAbrirFornecedores?: () => void;
  onAbrirProfissionais?: () => void;
  onAbrirRecibos?: () => void;
  onAbrirBoletos?: () => void;
  onAbrirVotacoes?: () => void;
  onAbrirAvisos?: () => void;
  onAbrirManutencoes?: () => void;
  onAbrirNotificacoes?: () => void;
  onAbrirReservas?: () => void;
  onAbrirEncomendas?: () => void;
  onAbrirVisitas?: () => void;
  onAbrirGas?: () => void;
  onAbrirApresentacoes?: () => void;
  onAbrirTelefones?: () => void;
  onAbrirPortal?: () => void;
  onAbrirAssistente?: () => void;
  onAbrirEquipe?: () => void;
}

interface CompetenciaResumo {
  id: number;
  ano: number;
  mes: number;
  status: string;
  periodo_str: string;
  observacao?: string | null;
  pdfs: { id: number; nome_arquivo: string; n_paginas: number | null }[];
}

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const MESES_ABREV = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

// Status concluídos (não-pendentes)
const STATUS_CONCLUIDOS = new Set(["auditada", "aprovada"]);

// Calcula o próximo (ano, mês) depois do mais recente já cadastrado.
function proximoPeriodo(
  comps: CompetenciaResumo[] | undefined,
): { ano: number; mes: number } {
  if (comps && comps.length > 0) {
    // lista vem em ordem cronológica crescente — o último é o mais recente
    const ultima = comps[comps.length - 1];
    if (ultima.mes === 12) return { ano: ultima.ano + 1, mes: 1 };
    return { ano: ultima.ano, mes: ultima.mes + 1 };
  }
  // sem competências: usa o mês corrente
  const hoje = new Date();
  return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
}

// Acentos neon (mesma paleta dos cards de Relatórios Detalhados), ciclados.
const HERO_ACCENTS: { hex: string; rgb: string }[] = [
  { hex: "#22C9B8", rgb: "34,201,184" },   // teal
  { hex: "#9684F6", rgb: "150,132,246" },  // violeta
  { hex: "#E8A13A", rgb: "232,161,58" },   // âmbar
  { hex: "#5A8CE6", rgb: "90,140,230" },   // azul
  { hex: "#34C484", rgb: "52,196,132" },   // verde
  { hex: "#E87BA4", rgb: "232,123,164" },  // rosa
];

// Botão de ação do hero: vidro escuro + filete neon no topo + ícone na cor do acento.
function AcaoNeon({
  icone: Icone, label, title, onClick, accentIndex = 0,
}: {
  icone: LucideIcon;
  label: string;
  title?: string;
  onClick?: () => void;
  accentIndex?: number;
}) {
  const acc = HERO_ACCENTS[accentIndex % HERO_ACCENTS.length];
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{ borderColor: "rgba(255,255,255,0.10)" }}
      className="group relative inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white/[0.08] hover:bg-white/[0.15] text-white text-sm font-medium transition-all overflow-hidden"
    >
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] transition-all group-hover:h-[3px]"
        style={{ background: acc.hex, boxShadow: `0 0 7px 1px rgba(${acc.rgb},0.85), 0 0 14px 2px rgba(${acc.rgb},0.45)` }}
      />
      <Icone size={16} style={{ color: acc.hex }} />
      {label}
    </button>
  );
}

export function CondominioPage({
  condominioId,
  onVoltar,
  onVerResultados,
  onAbrirBiblioteca,
  onAbrirFornecedores,
  onAbrirProfissionais,
  onAbrirRecibos,
  onAbrirBoletos,
  onAbrirVotacoes,
  onAbrirAvisos,
  onAbrirManutencoes,
  onAbrirNotificacoes,
  onAbrirReservas,
  onAbrirEncomendas,
  onAbrirVisitas,
  onAbrirGas,
  onAbrirApresentacoes,
  onAbrirTelefones,
  onAbrirPortal,
  onAbrirAssistente,
  onAbrirEquipe,
}: Props) {
  // Permissões: esconder ações de escrita para papéis somente-leitura.
  const { podeEscrever, podeLer, podeGerirEquipe, ehSindico, isDono } = usePermissoes();
  const podeEquipe = podeGerirEquipe(condominioId);
  // Visibilidade dos botões de gestão por permissão (o backend é a fonte da verdade;
  // isto só evita mostrar ao usuário ações que ele não pode concluir — ex.: um porteiro
  // que só tem "encomendas" não deve ver Boletos, Votações, Fiscal, etc.).
  const _e = (f: string) => podeEscrever(condominioId, f);
  const _l = (f: string) => podeLer(condominioId, f);
  // Chave de API deste condomínio (só o Dono vê/edita).
  const [chaveApiId, setChaveApiId] = useState<number | "">("");
  const [chavesApi, setChavesApi] = useState<{ id: number; nome: string; mascarada: string }[]>([]);
  const podeEditarCadastro = podeEscrever(condominioId, "condominio");
  const podeGerirBalancete = podeEscrever(condominioId, "balancete");
  const podeChamadaExtra = podeEscrever(condominioId, "chamadas_extras");

  const { data: cond } = useQuery<Condominio>({
    queryKey: ["condominio", condominioId],
    queryFn: () => api.get<Condominio>(`/condominios/${condominioId}`),
  });

  // Código do Condomínio (identidade estável para o crédito da CanadaTechBR).
  const { data: codigoData } = useQuery<{ codigo: string }>({
    queryKey: ["condominio-codigo", condominioId],
    queryFn: () => api.get<{ codigo: string }>(`/condominios/${condominioId}/codigo`),
  });

  // Modais de Fração Ideal e Tabela de Chamada Extra.
  const [showFracaoIdeal, setShowFracaoIdeal] = useState(false);
  const [showChamadaExtra, setShowChamadaExtra] = useState(false);

  // Documentos da Biblioteca vencidos / vencendo (para o aviso no painel).
  const { data: vencDocs } = useQuery<{
    total_vencidos: number;
    total_vencendo: number;
    itens: {
      id: number;
      titulo: string;
      categoria: string | null;
      data_validade: string | null;
      dias_para_vencer: number | null;
      vencido: boolean;
    }[];
  }>({
    queryKey: ["biblioteca-vencimentos", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/biblioteca/vencimentos`),
  });
  // O aviso reaparece a cada abertura do app; o usuário pode fechá-lo na sessão.
  const [avisoVencFechado, setAvisoVencFechado] = useState(false);

  const { data: comps, refetch: refetchComps } = useQuery<CompetenciaResumo[]>({
    queryKey: ["competencias", condominioId],
    queryFn: () =>
      api.get<CompetenciaResumo[]>(`/condominios/${condominioId}/competencias`),
    // Polling enquanto houver alguma competência processando
    refetchInterval: (query) => {
      const data = query.state.data;
      const algumaProcessando = data?.some((c) => c.status === "processando");
      return algumaProcessando ? 2000 : false;
    },
  });

  const qc = useQueryClient();

  // Forma de cobrança da água (config do condomínio; o auditor valida o rateio).
  const { data: aguaCfg } = useQuery<{ metodo: string; opcoes: Record<string, string> }>({
    queryKey: ["agua-rateio", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/agua-rateio`),
  });
  const salvarAgua = useMutation({
    mutationFn: (metodo: string) =>
      api.put(`/condominios/${condominioId}/agua-rateio`, { metodo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agua-rateio", condominioId] }),
  });

  // Limite de aprovação da convenção (0.1.92): detector de compra fracionada.
  const { data: limiteCfg } = useQuery<{ valor: number | null }>({
    queryKey: ["limite-aprovacao", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/limite-aprovacao`),
  });
  const [limiteTxt, setLimiteTxt] = useState<string | null>(null);
  const salvarLimite = useMutation({
    mutationFn: (valor: number | null) =>
      api.put(`/condominios/${condominioId}/limite-aprovacao`, { valor }),
    onSuccess: () => { setLimiteTxt(null); qc.invalidateQueries({ queryKey: ["limite-aprovacao", condominioId] }); },
  });
  const gravarLimite = () => {
    if (limiteTxt === null) return;
    const n = Number(limiteTxt.replace(/\./g, "").replace(",", "."));
    salvarLimite.mutate(limiteTxt.trim() && Number.isFinite(n) && n > 0 ? n : null);
  };

  // Mês ativo selecionado (padrão = mais recente).
  const [selecionadaId, setSelecionadaId] = useState<number | null>(null);

  // Visualizador das páginas do balancete de um mês (leitura, sem abrir o Windows).
  const [paginasMes, setPaginasMes] = useState<number[] | null>(null);
  const [compPaginasId, setCompPaginasId] = useState<number | null>(null);
  const verPaginasDoMes = async (comp: CompetenciaResumo) => {
    try {
      const info = await api.get<{ total_paginas: number }>(
        `/competencias/${comp.id}/paginas-info`,
      );
      const total =
        info.total_paginas ||
        comp.pdfs.reduce((a, p) => a + (p.n_paginas ?? 0), 0);
      if (total > 0) {
        setCompPaginasId(comp.id);
        setPaginasMes(Array.from({ length: total }, (_, i) => i + 1));
      } else {
        window.alert(
          "Este mês ainda não tem páginas para ver. Envie o PDF e processe o balancete primeiro.",
        );
      }
    } catch {
      window.alert("Não consegui abrir as páginas deste mês.");
    }
  };

  // Formulário "Novo mês" — o usuário escolhe mês/ano (default = próximo mês).
  const [novoMesAberto, setNovoMesAberto] = useState(false);
  const [formMes, setFormMes] = useState<number>(new Date().getMonth() + 1);
  const [formAno, setFormAno] = useState<number>(new Date().getFullYear());
  const [formArquivo, setFormArquivo] = useState<File | null>(null);

  useEffect(() => {
    if (!comps || comps.length === 0) return;
    const existe = comps.some((c) => c.id === selecionadaId);
    if (!existe) setSelecionadaId(comps[comps.length - 1].id);
  }, [comps, selecionadaId]);

  const lista = comps ?? [];
  const idxAtual = lista.findIndex((c) => c.id === selecionadaId);
  const compAtual = idxAtual >= 0 ? lista[idxAtual] : null;

  const criarMes = useMutation({
    // Cria o mês escolhido e, se um PDF foi anexado, já envia o arquivo para a
    // pasta certa (o usuário não precisa achar o caminho). Se o mês já existir,
    // reaproveita. Sem anexo, cria o mês vazio (o PDF pode ser enviado depois
    // pelo painel, ou colocado manualmente na pasta INPUT).
    mutationFn: async ({
      ano,
      mes,
      arquivo,
    }: {
      ano: number;
      mes: number;
      arquivo: File | null;
    }) => {
      let comp: CompetenciaResumo;
      try {
        comp = await api.post<CompetenciaResumo>(
          `/condominios/${condominioId}/competencias`,
          { ano, mes },
        );
      } catch (e) {
        // 409 = mês já existe -> reaproveita o existente para receber o PDF.
        if (e instanceof ApiError && e.status === 409) {
          const existente = (comps ?? []).find(
            (c) => c.ano === ano && c.mes === mes,
          );
          if (!existente) throw e;
          comp = existente;
        } else {
          throw e;
        }
      }
      if (arquivo) {
        await api.upload<CompetenciaResumo>(
          `/competencias/${comp.id}/upload-pdf`,
          arquivo,
        );
      }
      return comp;
    },
    onSuccess: async (nova) => {
      await qc.invalidateQueries({ queryKey: ["competencias", condominioId] });
      await refetchComps();
      setSelecionadaId(nova.id);
      setNovoMesAberto(false);
      setFormArquivo(null);
    },
  });

  // Abre o formulário já sugerindo o próximo mês da sequência (1 clique no caso
  // comum), mas o usuário pode escolher qualquer mês/ano para iniciar a série.
  const abrirNovoMes = () => {
    const { ano, mes } = proximoPeriodo(comps);
    setFormMes(mes);
    setFormAno(ano);
    setFormArquivo(null);
    setNovoMesAberto(true);
  };

  // --- Edição dos dados cadastrais do condomínio ---
  const [editando, setEditando] = useState(false);
  const [editForm, setEditForm] = useState({
    nome: "",
    numero_unidades: 1,
    cnpj: "",
    endereco: "",
    sindico_nome: "",
    sindico_email: "",
  });
  // Lista de unidades (uma por linha) — cadastro manual p/ detectar inadimplentes
  const [unidadesTexto, setUnidadesTexto] = useState("");
  // Contatos por apto (nome/e-mail/telefone) — opcionais; usados depois para o
  // síndico IMPORTAR os condôminos na tela Equipe. Chave = texto do apto.
  type Contato = { nome: string; email: string; telefone: string; qtd_moradores?: number | string; ocupacao?: string };
  const [contatos, setContatos] = useState<Record<string, Contato>>({});
  const [contatosAberto, setContatosAberto] = useState(false);
  // 0.1.83: o inquilino vê o Resumo do mês? (configuração do condomínio; padrão: não)
  const [inqVeResumo, setInqVeResumo] = useState(false);
  // 0.1.90: blocos / torres (salvos junto com o cadastro). mapaBlocos = escolha manual
  // (unidade > sigla); blocoAuto = o que o servidor reconheceu pelo nome.
  const [blocosForm, setBlocosForm] = useState<BlocosForm>(BLOCOS_VAZIO);
  const [mapaBlocos, setMapaBlocos] = useState<Record<string, string>>({});
  const [blocoAuto, setBlocoAuto] = useState<Record<string, string | null>>({});
  const [gPorBloco, setGPorBloco] = useState(true);
  const blocosAtivos = blocosForm.ativo ? blocosForm.blocos.filter((b) => b.sigla.trim()) : [];
  // --- Gerador de unidades (menos manual; opcional) ---
  const [geradorAberto, setGeradorAberto] = useState(false);
  const [modoGerador, setModoGerador] = useState<"padrao" | "faixa">("padrao");
  const [aplicarModo, setAplicarModo] = useState<"substituir" | "acrescentar">("substituir");
  // Padrão andar × apto
  const [gPrefixo, setGPrefixo] = useState("");
  const [gAndarIni, setGAndarIni] = useState(1);
  const [gAndarFim, setGAndarFim] = useState(10);
  const [gAptosPorAndar, setGAptosPorAndar] = useState(4);
  const [gDigitosApto, setGDigitosApto] = useState(2);
  // Faixa simples (X..Y)
  const [gFaixaIni, setGFaixaIni] = useState(1);
  const [gFaixaFim, setGFaixaFim] = useState(100);

  // Monta a lista conforme o modo escolhido (sem tocar no DOM).
  const construirUnidades = (): string[] => {
    const out: string[] = [];
    if (modoGerador === "padrao") {
      const ini = Math.min(gAndarIni, gAndarFim);
      const fim = Math.max(gAndarIni, gAndarFim);
      const porAndar = Math.max(1, gAptosPorAndar);
      const dig = Math.max(1, gDigitosApto);
      for (let andar = ini; andar <= fim; andar++) {
        for (let apt = 1; apt <= porAndar; apt++) {
          out.push(`${gPrefixo}${andar}${String(apt).padStart(dig, "0")}`);
        }
      }
    } else {
      const ini = Math.min(gFaixaIni, gFaixaFim);
      const fim = Math.max(gFaixaIni, gFaixaFim);
      for (let n = ini; n <= fim; n++) out.push(`${gPrefixo}${n}`);
    }
    return out;
  };

  // 0.1.90: "repetir para cada bloco" — A-101, A-102… B-101…
  const construirComBlocos = (): string[] => {
    const base = construirUnidades();
    if (!gPorBloco || !blocosAtivos.length) return base;
    return blocosAtivos.flatMap((b) => base.map((u) => `${b.sigla.trim().toUpperCase()}-${u}`));
  };

  // Prévia (primeiras/últimas) e total, para o usuário conferir antes de gerar.
  const previaGerador = construirComBlocos();

  const aplicarGerador = () => {
    const gerada = construirComBlocos();
    if (gerada.length === 0) return;
    if (aplicarModo === "substituir") {
      setUnidadesTexto(gerada.join("\n"));
    } else {
      const atuais = unidadesTexto.split(/[\n\t,;]+/).map((u) => u.trim()).filter(Boolean);
      const vistos = new Set(atuais.map((u) => u.toLowerCase()));
      for (const u of gerada) {
        if (!vistos.has(u.toLowerCase())) {
          atuais.push(u);
          vistos.add(u.toLowerCase());
        }
      }
      setUnidadesTexto(atuais.join("\n"));
    }
    setGeradorAberto(false);
  };

  const abrirEditar = () => {
    if (!cond) return;
    setEditForm({
      nome: cond.nome ?? "",
      numero_unidades: cond.numero_unidades ?? 1,
      cnpj: cond.cnpj ?? "",
      endereco: cond.endereco ?? "",
      sindico_nome: cond.sindico_nome ?? "",
      sindico_email: cond.sindico_email ?? "",
    });
    setEditando(true);
    // Desce a tela até o painel de edição (senão a sensação é de "não fez nada").
    setTimeout(() => {
      document.getElementById("painel-editar-condominio")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    // Carrega as unidades cadastradas
    api
      .get<{ unidades: string[]; moradores?: Record<string, Contato>; inquilino_ve_resumo?: boolean }>(`/condominios/${condominioId}/unidades`)
      .then((r) => {
        setUnidadesTexto((r.unidades ?? []).join("\n"));
        setContatos(r.moradores ?? {});
        setInqVeResumo(!!r.inquilino_ve_resumo);
      })
      .catch(() => setUnidadesTexto(""));
    api
      .get<{ rotulo: string; blocos: { id: number; nome: string; sigla: string }[];
        unidades: { unidade: string; bloco_sigla: string | null; origem: string | null }[] }>(`/condominios/${condominioId}/blocos`)
      .then((r) => {
        setBlocosForm({ ativo: r.blocos.length > 0, rotulo: r.rotulo === "Torre" ? "Torre" : "Bloco",
          blocos: r.blocos.map((b) => ({ id: b.id, nome: b.nome, sigla: b.sigla })) });
        setMapaBlocos(Object.fromEntries(r.unidades.filter((u) => u.origem === "manual").map((u) => [u.unidade, u.bloco_sigla ?? ""])));
        setBlocoAuto(Object.fromEntries(r.unidades.map((u) => [u.unidade, u.bloco_sigla])));
      })
      .catch(() => setBlocosForm(BLOCOS_VAZIO));
    // Chave de API (só Dono): lista + atribuição atual.
    if (isDono) {
      api.get<{ chaves: { id: number; nome: string; mascarada: string }[] }>("/chaves-api")
        .then((r) => setChavesApi(r.chaves ?? [])).catch(() => setChavesApi([]));
      api.get<{ chave_api_id: number | null }>(`/condominios/${condominioId}/chave-api`)
        .then((r) => setChaveApiId(r.chave_api_id ?? "")).catch(() => setChaveApiId(""));
    }
  };
  // Bloquear / desbloquear o condomínio (só o Dono). Enquanto bloqueado, ninguém
  // além do Dono pode alterar nada nele.
  const bloquear = useMutation({
    mutationFn: (args: { bloquear: boolean; motivo?: string }) =>
      args.bloquear
        ? api.post(`/condominios/${condominioId}/bloquear`, { motivo: args.motivo ?? "" })
        : api.post(`/condominios/${condominioId}/desbloquear`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["condominio", condominioId] });
      qc.invalidateQueries({ queryKey: ["condominios"] });
    },
  });

  const editar = useMutation({
    mutationFn: async () => {
      await api.patch<Condominio>(`/condominios/${condominioId}`, {
        nome: editForm.nome,
        cnpj: editForm.cnpj || null,
        endereco: editForm.endereco || null,
        sindico_nome: editForm.sindico_nome || null,
        sindico_email: editForm.sindico_email || null,
      });
      // Aceita colar de planilha/CSV: separa por quebra de linha, tabulação,
      // ponto-e-vírgula ou vírgula. O backend ainda remove duplicados/vazios.
      const unidades = unidadesTexto
        .split(/[\n\t,;]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      // PUT /unidades também ajusta numero_unidades = len(unidades).
      // Se nenhuma unidade foi informada, mantém o número digitado.
      if (unidades.length > 0) {
        await api.put<{ unidades: string[] }>(
          `/condominios/${condominioId}/unidades`,
          { unidades },
        );
      } else {
        await api.patch<Condominio>(`/condominios/${condominioId}`, {
          numero_unidades: editForm.numero_unidades,
        });
      }
      // 0.1.90: blocos / torres (desligado = arquiva os blocos; nada se apaga).
      const siglasOk = new Set(blocosAtivos.map((b) => b.sigla.trim().toUpperCase()));
      await api.put(`/condominios/${condominioId}/blocos`, {
        rotulo: blocosForm.rotulo,
        blocos: blocosForm.ativo ? blocosAtivos.map((b) => ({ id: b.id, nome: b.nome.trim() || b.sigla, sigla: b.sigla.trim().toUpperCase() })) : [],
        mapa: Object.fromEntries(unidades.filter((u) => mapaBlocos[u] && siglasOk.has(mapaBlocos[u])).map((u) => [u, mapaBlocos[u]])),
      });
      // Contatos por apto (só os aptos atuais; o backend descarta vazios).
      const setAtuais = new Set(unidades);
      const moradores: Record<string, Contato> = {};
      for (const apto of unidades) {
        const c = contatos[apto];
        if (c && (c.nome || c.email || c.telefone || c.qtd_moradores || c.ocupacao)) moradores[apto] = c;
      }
      // remove contatos órfãos (de aptos que não existem mais) — implicitamente
      // não incluídos acima.
      void setAtuais;
      await api.put(`/condominios/${condominioId}/moradores`, { moradores, inquilino_ve_resumo: inqVeResumo });
      // Chave de API do condomínio (só o Dono edita).
      if (isDono) {
        await api.put(`/condominios/${condominioId}/chave-api`, {
          chave_api_id: chaveApiId === "" ? null : Number(chaveApiId),
        });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["condominio", condominioId] });
      await qc.invalidateQueries({ queryKey: ["condominios"] });
      await qc.invalidateQueries({ queryKey: ["blocos", condominioId] });
      setEditando(false);
    },
  });

  const irPara = (delta: number) => {
    if (idxAtual < 0) return;
    const novo = idxAtual + delta;
    if (novo >= 0 && novo < lista.length) setSelecionadaId(lista[novo].id);
  };

  return (
    <div>
      {/* Visualizador das páginas do balancete (modal, leitura) */}
      {compPaginasId != null && (
        <VisualizadorPaginas
          competenciaId={compPaginasId}
          paginas={paginasMes ?? []}
          aberto={paginasMes != null}
          onFechar={() => setPaginasMes(null)}
        />
      )}

      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      {/* Hero do condomínio */}
      <div
        className="relative overflow-hidden rounded-2xl p-6"
        style={{
          background:
            "radial-gradient(900px 380px at 88% -20%, rgba(34,201,184,0.20), transparent 60%)," +
            "radial-gradient(700px 420px at 4% 120%, rgba(60,110,190,0.30), transparent 60%)," +
            "linear-gradient(155deg,#0f1d33 0%,#16294A 55%,#1d3358 100%)",
          boxShadow: "0 20px 50px rgba(8,15,30,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl"
          style={{ background: "#22C9B8", boxShadow: "0 0 10px 1px rgba(34,201,184,0.9), 0 0 22px 3px rgba(34,201,184,0.5)" }}
        />
        <div className="absolute -top-8 -right-6 opacity-[0.07] text-white">
          <Building2 size={140} />
        </div>
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <LogoCondominio condominioId={condominioId} podeEditar={ehSindico(condominioId) || isDono} />
            <h2 className="text-2xl font-bold text-white truncate">
              {cond?.nome ?? "Carregando..."}
            </h2>
            <p className="text-sm text-white/80 mt-1">
              {cond?.numero_unidades} unidades
              {cond?.sindico_nome && <> · Síndico: {cond.sindico_nome}</>}
            </p>
            {cond?.bloqueado_em && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-400/90 text-amber-950 text-[12px] font-semibold">
                <Lock size={13} />
                Bloqueado (em manutenção){cond.bloqueado_motivo ? ` — ${cond.bloqueado_motivo}` : ""}. Alterações suspensas.
              </div>
            )}
            {codigoData?.codigo && (
              <p className="text-[11px] text-white/70 mt-1"
                title="Informe este código à CanadaTechBR para receber/ajustar o crédito mensal">
                Código do Condomínio:{" "}
                <span className="font-mono text-white/90">{codigoData.codigo}</span>
              </p>
            )}
            {cond && cond.contas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {cond.contas.map((c) => (
                  <span
                    key={c.id}
                    className="px-2 py-0.5 bg-white/15 text-white text-[11px] rounded-md backdrop-blur-sm"
                    title={c.nome}
                  >
                    {c.codigo}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {isDono && (
              <button
                type="button"
                onClick={() => {
                  if (cond?.bloqueado_em) {
                    bloquear.mutate({ bloquear: false });
                  } else {
                    const motivo = window.prompt(
                      "Bloquear este condomínio (suspende alterações de todos, menos você). Motivo (opcional):",
                      "",
                    );
                    if (motivo === null) return; // cancelou
                    bloquear.mutate({ bloquear: true, motivo });
                  }
                }}
                disabled={bloquear.isPending}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md backdrop-blur-sm transition ${
                  cond?.bloqueado_em
                    ? "bg-emerald-400/90 hover:bg-emerald-400 text-emerald-950"
                    : "bg-amber-400/90 hover:bg-amber-400 text-amber-950"
                }`}
                title={cond?.bloqueado_em ? "Liberar o condomínio" : "Bloquear temporariamente (suspende alterações)"}
              >
                {cond?.bloqueado_em ? <Unlock size={16} /> : <Lock size={16} />}
                {cond?.bloqueado_em ? "Desbloquear" : "Bloquear"}
              </button>
            )}
            {podeEditarCadastro && (
              <AcaoNeon accentIndex={0} icone={Pencil} label="Cadastro" onClick={abrirEditar}
                title="Cadastro do condomínio (editar dados)" />
            )}
            {compAtual && compAtual.pdfs.length > 0 && _l("balancete") && (
              <AcaoNeon accentIndex={1} icone={BookOpen} label="Ver balancete"
                onClick={() => verPaginasDoMes(compAtual)}
                title={`Ver as páginas do balancete de ${MESES_ABREV[compAtual.mes - 1]}/${compAtual.ano} — dentro do app, sem abrir o Windows`} />
            )}
            {/* Histórico, Fornecedores e Auditoria Fiscal migraram para o painel
                "Relatórios Detalhados" dentro do Dashboard (0.1.53). */}
            {onAbrirAssistente && _l("assistente") && (
              <AcaoNeon accentIndex={2} icone={Sparkles} label="Assistente" onClick={onAbrirAssistente}
                title="Buscar nas páginas e perguntar (limitado a este condomínio)" />
            )}
            {onAbrirBiblioteca && _l("biblioteca") && (
              <AcaoNeon accentIndex={3} icone={BookOpen} label="Biblioteca" onClick={onAbrirBiblioteca}
                title="Biblioteca de documentos do condomínio (convenção, contratos, alvarás…)" />
            )}
            {onAbrirProfissionais && _l("profissionais") && (
              <AcaoNeon accentIndex={4} icone={Wrench} label="Profissionais" onClick={onAbrirProfissionais}
                title="Agenda de profissionais/prestadores (hidráulico, eletricista, pintor, empresas de serviço…)" />
            )}
            {onAbrirFornecedores && _e("condominio") && (
              <AcaoNeon accentIndex={5} icone={Tag} label="Fornecedores (histórico)" onClick={onAbrirFornecedores}
                title="Cadastro dos fornecedores usados no Histórico de Fornecedores (nomes/categorias)" />
            )}
            {onAbrirRecibos && _e("recibos") && (
              <AcaoNeon accentIndex={6} icone={Receipt} label="Recibos" onClick={onAbrirRecibos}
                title="Recibos digitais (numeração própria + QR) — o síndico emite no lugar do recibo à mão" />
            )}
            {onAbrirBoletos && _e("boletos") && (
              <AcaoNeon accentIndex={7} icone={FileText} label="Boletos" onClick={onAbrirBoletos}
                title="Boletos do mês (2ª via) — suba o PDF com todos os boletos; o morador baixa só o dele no Portal (DOC)" />
            )}
            {onAbrirVotacoes && _e("votacoes") && (
              <AcaoNeon accentIndex={8} icone={Vote} label="Votações" onClick={onAbrirVotacoes}
                title="Assembleias (convocação, edital, lista de presença) e votações online com ata" />
            )}
            {onAbrirAvisos && _e("avisos") && (
              <AcaoNeon accentIndex={9} icone={Megaphone} label="Avisos" onClick={onAbrirAvisos}
                title="Avisos do condomínio — publique comunicados que o morador vê no Portal (e recebe por e-mail/notificação)" />
            )}
            {onAbrirManutencoes && _e("manutencoes") && (
              <AcaoNeon accentIndex={10} icone={Wrench} label="Manutenções" onClick={onAbrirManutencoes}
                title="Manutenções do prédio — cadastre e acompanhe o calendário; o morador vê no Portal" />
            )}
            {onAbrirNotificacoes && _e("notificacoes") && (
              <AcaoNeon accentIndex={11} icone={Gavel} label="Notificações" onClick={onAbrirNotificacoes}
                title="Notificações/infrações — registre ocorrências por unidade e gere a carta; o morador vê só as da sua unidade" />
            )}
            {onAbrirReservas && _e("reservas") && (
              <AcaoNeon accentIndex={12} icone={CalendarClock} label="Reservas" onClick={onAbrirReservas}
                title="Reservas — cadastre os recursos (salão, churrasqueira…) e aprove pedidos; o morador solicita pelo Portal" />
            )}
            {onAbrirEncomendas && _e("encomendas") && (
              <AcaoNeon accentIndex={13} icone={Package} label="Encomendas" onClick={onAbrirEncomendas}
                title="Encomendas — a portaria registra a chegada (gera código e avisa o apto) e dá baixa na entrega" />
            )}
            {onAbrirVisitas && _l("visitas") && (
              <AcaoNeon accentIndex={18} icone={DoorOpen} label="Visitas / Portaria" onClick={onAbrirVisitas}
                title="Liberação de visitas — convites com QR, chegada presencial (aprovação do morador) e livro de entradas/saídas" />
            )}
            {onAbrirApresentacoes && _l("apresentacoes") && (
              <AcaoNeon accentIndex={2} icone={Presentation} label="Apresentação" onClick={onAbrirApresentacoes}
                title="Apresentação aos condôminos — slides de prestação de contas gerados com IA a partir dos balancetes auditados" />
            )}
            {onAbrirGas && _l("gas") && (
              <AcaoNeon accentIndex={7} icone={Flame} label="Controle de Gás" onClick={onAbrirGas}
                title="Controle e rateio de gás — leituras dos medidores, rateio por apartamento e auditoria da conta da concessionária" />
            )}
            {onAbrirTelefones && (ehSindico(condominioId) || isDono) && (
              <AcaoNeon accentIndex={14} icone={PhoneCall} label="Telefones de Emergência" onClick={onAbrirTelefones}
                title="Telefones de Emergência — crie/edite a lista que aparece no Portal do Condômino" />
            )}
            {onAbrirPortal && ehSindico(condominioId) && (
              <button
                type="button"
                onClick={onAbrirPortal}
                className="inline-flex items-center gap-2 px-3 py-2 bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold rounded-lg shadow-sm transition"
                title="Portal do Condômino — pré-visualize como o morador vê o app no celular"
              >
                <Smartphone size={16} />
                Portal
              </button>
            )}
            {podeEquipe && onAbrirEquipe && (
              <AcaoNeon accentIndex={15} icone={Users} label="Cad. Acesso" onClick={onAbrirEquipe}
                title="Cadastro de acesso: cadastrar usuários (subsíndico, conselho, morador), papéis e permissões" />
            )}
            {podeChamadaExtra && (
              <AcaoNeon accentIndex={16} icone={Percent} label="Fração Ideal" onClick={() => setShowFracaoIdeal(true)}
                title="Cadastrar a fração ideal (%) de cada unidade — base do rateio da chamada extra" />
            )}
            {podeChamadaExtra && (
              <AcaoNeon accentIndex={17} icone={Coins} label="Chamada Extra" onClick={() => setShowChamadaExtra(true)}
                title="Gerar a tabela de chamada extra (valor por unidade e por parcela) para imprimir/enviar" />
            )}
          </div>
        </div>
      </div>

      {showFracaoIdeal && (
        <FracaoIdealModal
          condominioId={condominioId}
          condominioNome={cond?.nome}
          onFechar={() => setShowFracaoIdeal(false)}
        />
      )}
      {showChamadaExtra && (
        <ChamadaExtraModal
          condominioId={condominioId}
          condominioNome={cond?.nome}
          onFechar={() => setShowChamadaExtra(false)}
        />
      )}

      {/* Aviso de vencimento de documentos da Biblioteca (reaparece a cada abertura;
          o usuário pode retirar da tela clicando no X). Lista os vencidos (vermelho)
          e os que estão para vencer (amarelo). */}
      {onAbrirBiblioteca && !avisoVencFechado && vencDocs &&
        (vencDocs.total_vencidos > 0 || vencDocs.total_vencendo > 0) && (
        <div className={`mt-4 rounded-xl border-l-4 px-4 py-3 text-sm shadow-sm ${
          vencDocs.total_vencidos > 0
            ? "border-rose-500 bg-rose-50 text-rose-900"
            : "border-amber-500 bg-amber-50 text-amber-900"}`}>
          <div className="flex items-center gap-2.5">
            <AlertCircle size={20} className={`shrink-0 ${vencDocs.total_vencidos > 0 ? "text-rose-600" : "text-amber-600"}`} />
            <span className="font-semibold">
              Documentos da Biblioteca com validade
              {vencDocs.total_vencidos > 0 && <> · <b>{vencDocs.total_vencidos} vencido(s)</b></>}
              {vencDocs.total_vencendo > 0 && <> · {vencDocs.total_vencendo} a vencer</>}
            </span>
            <div className="ml-auto flex items-center gap-3 shrink-0">
              <button type="button" onClick={onAbrirBiblioteca}
                className="text-xs font-bold underline hover:opacity-80">
                Ver Biblioteca
              </button>
              <button type="button" onClick={() => setAvisoVencFechado(true)}
                title="Retirar este aviso (volta a aparecer quando você reabrir o app)"
                className="text-current/70 hover:text-current">
                <X size={16} />
              </button>
            </div>
          </div>
          <ul className="mt-2 space-y-1 pl-1">
            {(vencDocs.itens ?? [])
              .slice()
              .sort((a, b) => (a.dias_para_vencer ?? 0) - (b.dias_para_vencer ?? 0))
              .slice(0, 8)
              .map((d) => {
                const dv = d.data_validade
                  ? new Date(d.data_validade + "T00:00:00").toLocaleDateString("pt-BR")
                  : "—";
                const dias = d.dias_para_vencer ?? 0;
                const txt = d.vencido
                  ? `venceu ${dv} (há ${Math.abs(dias)} dia${Math.abs(dias) === 1 ? "" : "s"})`
                  : `vence ${dv} (em ${dias} dia${dias === 1 ? "" : "s"})`;
                return (
                  <li key={d.id} className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${d.vencido ? "bg-rose-500" : "bg-amber-500"}`} />
                    <span className="truncate">
                      <b>{d.titulo}</b>
                      {d.categoria ? <span className="opacity-70"> · {d.categoria}</span> : null}
                      <span className="opacity-80"> — {txt}</span>
                    </span>
                  </li>
                );
              })}
            {(vencDocs.itens?.length ?? 0) > 8 && (
              <li className="opacity-70 text-xs pl-4">
                +{(vencDocs.itens!.length - 8)} outro(s) — veja na Biblioteca.
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Painel de edição dos dados do condomínio */}
      {editando && (
        <div id="painel-editar-condominio" className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm p-5" style={{ scrollMarginTop: 16 }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Editar condomínio
            </h3>
            <button
              type="button"
              onClick={() => setEditando(false)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Nome do condomínio
              </span>
              <input
                value={editForm.nome}
                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Nome curto (pasta)
              </span>
              <input
                value={cond?.nome_pasta ?? ""}
                disabled
                title="Não editável — é o nome da pasta no disco"
                className="w-full px-2 py-1.5 border border-slate-200 bg-slate-50 text-slate-400 rounded-md text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Nº de unidades
              </span>
              <input
                type="number"
                min={1}
                value={editForm.numero_unidades}
                onChange={(e) =>
                  setEditForm({ ...editForm, numero_unidades: Number(e.target.value) })
                }
                disabled={
                  unidadesTexto.split("\n").map((u) => u.trim()).filter(Boolean).length > 0
                }
                title="Definido automaticamente pela lista de unidades abaixo (quando preenchida)"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700 disabled:bg-slate-50 disabled:text-slate-400"
              />
            </label>
            <BlocosCadastro valor={blocosForm} onChange={setBlocosForm} />
            <div className="block sm:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <span className="block text-[11px] font-medium text-slate-500">
                  Unidades / apartamentos (uma por linha)
                </span>
                <button
                  type="button"
                  onClick={() => setGeradorAberto((v) => !v)}
                  className="text-[11px] font-medium text-brand-700 hover:text-brand-800 hover:underline"
                  title="Gerar a lista automaticamente por um padrão (andar × apto) ou uma faixa — em vez de digitar um a um"
                >
                  {geradorAberto ? "Fechar gerador" : "✨ Gerar unidades automaticamente"}
                </button>
              </div>

              {geradorAberto && (
                <div className="mb-2 rounded-lg border border-brand-200 bg-brand-50/50 p-3 space-y-3">
                  {/* Escolha do modo */}
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setModoGerador("padrao")}
                      className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${
                        modoGerador === "padrao"
                          ? "bg-brand-700 text-white"
                          : "bg-white text-slate-600 border border-slate-300"
                      }`}
                    >
                      Andar × apto (101, 102…)
                    </button>
                    <button
                      type="button"
                      onClick={() => setModoGerador("faixa")}
                      className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${
                        modoGerador === "faixa"
                          ? "bg-brand-700 text-white"
                          : "bg-white text-slate-600 border border-slate-300"
                      }`}
                    >
                      Faixa simples (1 até N)
                    </button>
                  </div>

                  {modoGerador === "padrao" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Prefixo (opcional)</span>
                        <input
                          value={gPrefixo}
                          onChange={(e) => setGPrefixo(e.target.value)}
                          placeholder="ex.: Ap "
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Andar inicial</span>
                        <input
                          type="number"
                          value={gAndarIni}
                          onChange={(e) => setGAndarIni(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Andar final</span>
                        <input
                          type="number"
                          value={gAndarFim}
                          onChange={(e) => setGAndarFim(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Aptos por andar</span>
                        <input
                          type="number"
                          min={1}
                          value={gAptosPorAndar}
                          onChange={(e) => setGAptosPorAndar(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Dígitos do apto</span>
                        <select
                          value={gDigitosApto}
                          onChange={(e) => setGDigitosApto(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                          title="Quantos dígitos o número do apto tem no andar. 2 → 101, 102 (padrão). 1 → 11, 12."
                        >
                          <option value={1}>1 (ex.: 11, 12)</option>
                          <option value={2}>2 (ex.: 101, 102)</option>
                          <option value={3}>3 (ex.: 1001, 1002)</option>
                        </select>
                      </label>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Prefixo (opcional)</span>
                        <input
                          value={gPrefixo}
                          onChange={(e) => setGPrefixo(e.target.value)}
                          placeholder="ex.: Vaga "
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">De</span>
                        <input
                          type="number"
                          value={gFaixaIni}
                          onChange={(e) => setGFaixaIni(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] text-slate-500 mb-0.5">Até</span>
                        <input
                          type="number"
                          value={gFaixaFim}
                          onChange={(e) => setGFaixaFim(Number(e.target.value))}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </label>
                    </div>
                  )}

                  {/* Prévia */}
                  <div className="text-[11px] text-slate-600 bg-white rounded border border-slate-200 px-2 py-1.5">
                    <strong className="text-slate-700">{previaGerador.length}</strong> unidade(s):{" "}
                    <span className="font-mono text-slate-500">
                      {previaGerador.slice(0, 4).join(", ")}
                      {previaGerador.length > 6 && ` … ${previaGerador.slice(-2).join(", ")}`}
                    </span>
                  </div>

                  {blocosAtivos.length > 0 && (
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <input type="checkbox" checked={gPorBloco} onChange={(e) => setGPorBloco(e.target.checked)} />
                      Repetir para cada {blocosForm.rotulo.toLowerCase()} ({blocosAtivos.map((b) => b.sigla.toUpperCase()).join(", ")}) —
                      fica <span className="font-mono">{blocosAtivos[0].sigla.toUpperCase()}-101</span>
                    </label>
                  )}

                  {/* Substituir x acrescentar + gerar */}
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1 text-[11px] text-slate-600">
                      <input
                        type="radio"
                        checked={aplicarModo === "substituir"}
                        onChange={() => setAplicarModo("substituir")}
                      />
                      Substituir a lista
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-slate-600">
                      <input
                        type="radio"
                        checked={aplicarModo === "acrescentar"}
                        onChange={() => setAplicarModo("acrescentar")}
                      />
                      Acrescentar (sem repetir)
                    </label>
                    <button
                      type="button"
                      onClick={aplicarGerador}
                      disabled={previaGerador.length === 0}
                      className="ml-auto text-xs font-medium px-3 py-1.5 rounded-md bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-50"
                    >
                      Gerar {previaGerador.length} na lista
                    </button>
                  </div>
                </div>
              )}

              <textarea
                value={unidadesTexto}
                onChange={(e) => setUnidadesTexto(e.target.value)}
                rows={6}
                placeholder={"101\n102\n201\n202\n..."}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700 font-mono"
              />
              <span className="block text-[11px] text-slate-400 mt-1">
                Cadastre o número de cada apartamento para o sistema saber o total
                e apontar quem não pagou (ex.: 9/10). O nº de unidades passa a ser a
                quantidade de linhas preenchidas. Você também pode{" "}
                <strong className="text-slate-500">colar uma coluna do Excel</strong> aqui
                (copie a coluna e Ctrl+V).
                {unidadesTexto.trim() && (
                  <>
                    {" "}
                    <strong className="text-slate-600">
                      {
                        unidadesTexto
                          .split(/[\n\t,;]+/)
                          .map((u) => u.trim())
                          .filter(Boolean).length
                      }{" "}
                      unidade(s) na lista.
                    </strong>
                  </>
                )}
              </span>

              {/* Prévia dos aptos em grade (várias colunas), para não ficar em
                  uma "tripa" vertical. A caixa acima segue sendo a fonte editável. */}
              {unidadesTexto.trim() && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {unidadesTexto
                    .split(/[\n\t,;]+/)
                    .map((u) => u.trim())
                    .filter(Boolean)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .map((apto) => (
                      <span
                        key={apto}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[12px] font-mono text-slate-600"
                      >
                        {apto}
                      </span>
                    ))}
                </div>
              )}

              {/* Contatos por apto (opcional) — casa com os aptos gerados/colados
                  acima e alimenta a importação de condôminos na tela Equipe. */}
              {unidadesTexto.trim() && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setContatosAberto((v) => !v)}
                    className="text-[12px] text-brand-700 hover:underline"
                  >
                    {contatosAberto ? "Fechar contatos dos moradores" : "👥 Preencher contatos dos moradores (opcional)"}
                  </button>
                  {contatosAberto && (
                    <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-slate-50/60">
                      <p className="text-[11px] text-slate-500 mb-2">
                        Ao lado de cada apto, informe (opcional) <b>nome</b>, <b>e-mail</b> e
                        <b> telefone</b> do <b>proprietário</b>. Depois, na tela <b>Equipe</b>, o síndico importa todos
                        de uma vez como condôminos — quem tiver e-mail cria a própria senha pelo
                        “Esqueci minha senha”. Apto <b>alugado</b>: marque a ocupação e cadastre o
                        inquilino no <b>Cad. Acesso</b>, no bloco do apartamento.
                      </p>
                      <label className="flex items-center gap-2 text-[12px] text-slate-700 mb-2">
                        <input type="checkbox" checked={inqVeResumo} onChange={(e) => setInqVeResumo(e.target.checked)} />
                        Inquilinos podem ver o <b>Resumo do mês</b> (prestação de contas) no Portal
                      </label>
                      <div className="max-h-72 overflow-y-auto">
                        <table className="min-w-full text-[13px]">
                          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                              <th className="text-left p-1 w-24">Apto</th>
                              {blocosAtivos.length > 0 && <th className="text-left p-1 w-28">{blocosForm.rotulo}</th>}
                              <th className="text-left p-1">Nome (proprietário)</th>
                              <th className="text-left p-1">E-mail</th>
                              <th className="text-left p-1 w-32">Telefone</th>
                              <th className="text-left p-1 w-24">Qtd. moradores</th>
                              <th className="text-left p-1 w-28">Ocupação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unidadesTexto
                              .split(/[\n\t,;]+/)
                              .map((u) => u.trim())
                              .filter(Boolean)
                              .filter((v, i, a) => a.indexOf(v) === i)
                              .map((apto) => {
                                const c = contatos[apto] ?? { nome: "", email: "", telefone: "", qtd_moradores: "" };
                                const upd = (campo: keyof Contato, val: string) =>
                                  setContatos((prev) => ({ ...prev, [apto]: { ...c, [campo]: val } }));
                                return (
                                  <tr key={apto}>
                                    <td className="p-1 font-mono text-slate-700">{apto}</td>
                                    {blocosAtivos.length > 0 && (
                                      <td className="p-1">
                                        <select value={mapaBlocos[apto] ?? ""} onChange={(e) => setMapaBlocos((prev) => ({ ...prev, [apto]: e.target.value }))}
                                          className="w-full px-1 py-1 border border-slate-300 rounded text-[13px] bg-white"
                                          title="Automático = reconhecido pelo nome da unidade (A-101 é do A)">
                                          <option value="">{blocoAuto[apto] ? `Automático (${blocoAuto[apto]})` : "Automático"}</option>
                                          {blocosAtivos.map((b) => <option key={b.sigla} value={b.sigla.toUpperCase()}>{b.nome || b.sigla}</option>)}
                                        </select>
                                      </td>
                                    )}
                                    <td className="p-1"><input value={c.nome} onChange={(e) => upd("nome", e.target.value)} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[13px]" /></td>
                                    <td className="p-1"><input type="email" value={c.email} onChange={(e) => upd("email", e.target.value)} placeholder="opcional" className="w-full px-1.5 py-1 border border-slate-300 rounded text-[13px]" /></td>
                                    <td className="p-1"><input value={c.telefone} onChange={(e) => upd("telefone", e.target.value)} placeholder="opcional" className="w-full px-1.5 py-1 border border-slate-300 rounded text-[13px]" /></td>
                                    <td className="p-1"><input type="number" min={0} value={c.qtd_moradores ?? ""} onChange={(e) => upd("qtd_moradores", e.target.value)} placeholder="opcional" className="w-full px-1.5 py-1 border border-slate-300 rounded text-[13px] text-right" /></td>
                                    <td className="p-1">
                                      <select value={c.ocupacao || "proprio"} onChange={(e) => upd("ocupacao", e.target.value)}
                                        className="w-full px-1 py-1 border border-slate-300 rounded text-[13px] bg-white">
                                        <option value="proprio">Próprio</option>
                                        <option value="alugado">Alugado</option>
                                      </select>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {isDono && (
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">
                  Chave de API deste condomínio (só o dono)
                </span>
                <select
                  value={chaveApiId}
                  onChange={(e) => setChaveApiId(e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                >
                  <option value="">Usar a chave do Dono (padrão)</option>
                  {chavesApi.map((k) => (
                    <option key={k.id} value={k.id}>{k.nome} ({k.mascarada})</option>
                  ))}
                </select>
                <span className="block text-[11px] text-slate-400 mt-1">
                  Cadastre chaves em <b>Chaves</b> (no topo). "Padrão" usa a chave do servidor.
                </span>
              </label>
            )}
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                CNPJ
              </span>
              <input
                value={editForm.cnpj}
                onChange={(e) => setEditForm({ ...editForm, cnpj: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Endereço
              </span>
              <input
                value={editForm.endereco}
                onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Nome do síndico
              </span>
              <input
                value={editForm.sindico_nome}
                onChange={(e) =>
                  setEditForm({ ...editForm, sindico_nome: e.target.value })
                }
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                E-mail do síndico
              </span>
              <input
                type="email"
                value={editForm.sindico_email}
                onChange={(e) =>
                  setEditForm({ ...editForm, sindico_email: e.target.value })
                }
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Como a água é cobrada?
                <span className="text-slate-400"> (auditar o rateio)</span>
              </span>
              <select
                value={aguaCfg?.metodo ?? ""}
                onChange={(e) => salvarAgua.mutate(e.target.value)}
                disabled={salvarAgua.isPending}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white text-slate-700"
              >
                <option value="">Não informado</option>
                {Object.entries(
                  aguaCfg?.opcoes && Object.keys(aguaCfg.opcoes).length
                    ? aguaCfg.opcoes
                    : OPCOES_AGUA,
                ).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <span className="block text-[11px] text-slate-400 mt-1">
                Salva sozinho ao escolher. Se informado, o sistema confere se a água foi rateada
                por esse critério e avisa divergências nos Pontos de Atenção. "Nº de moradores"
                usa a quantidade por apto (opcional, no cadastro de moradores).
              </span>
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Limite de aprovação da convenção (R$)
                <span className="text-slate-400"> (despesas extraordinárias)</span>
              </span>
              <input
                inputMode="decimal"
                placeholder="ex.: 5.000,00"
                value={limiteTxt ?? (limiteCfg?.valor != null
                  ? limiteCfg.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "")}
                onChange={(e) => setLimiteTxt(e.target.value)}
                onBlur={gravarLimite}
                onKeyDown={(e) => { if (e.key === "Enter") gravarLimite(); }}
                disabled={salvarLimite.isPending}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
              />
              <span className="block text-[11px] text-slate-400 mt-1">
                Valor acima do qual a convenção exige aprovação do conselho ou da assembleia. Salva ao
                sair do campo. Se informado, os Pontos de Atenção avisam quando a conta principal paga
                uma despesa extraordinária acima dele, ou várias da mesma natureza no mês que somadas
                passam dele. Despesas do dia a dia (água, luz, gás, administradora, salários…), as que
                se repetem todo mês, parcelas e gastos do Fundo de Reserva não entram. Em branco, o
                aviso fica desligado.
              </span>
            </label>
          </div>
          {editar.isError && (
            <p className="mt-3 text-xs text-rose-700">
              Não foi possível salvar. Tente de novo.
            </p>
          )}
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => editar.mutate()}
              disabled={editar.isPending || !editForm.nome.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md"
            >
              {editar.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Salvar
            </button>
            <button
              type="button"
              onClick={() => setEditando(false)}
              className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Navegador de meses */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Competência
          </h3>
          {podeGerirBalancete && (
            <button
              type="button"
              onClick={abrirNovoMes}
              disabled={novoMesAberto}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-brand-400 hover:text-brand-700 disabled:opacity-50 text-slate-700 text-xs font-medium rounded-md shadow-sm transition"
              title="Cadastra um mês — você escolhe o mês/ano e pode anexar o PDF do balancete"
            >
              <CalendarPlus size={14} />
              Novo mês
            </button>
          )}
        </div>

        {novoMesAberto && (
          <div className="mb-3 p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Mês
                </label>
                <select
                  value={formMes}
                  onChange={(e) => setFormMes(Number(e.target.value))}
                  className="px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700 bg-white"
                >
                  {MESES.map((m, i) => (
                    <option key={i} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Ano
                </label>
                <input
                  type="number"
                  value={formAno}
                  min={2020}
                  max={2099}
                  onChange={(e) => setFormAno(Number(e.target.value))}
                  className="w-24 px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-700"
                />
              </div>
            </div>

            {/* PDF do balancete: arrastar ou escolher — o sistema coloca na pasta certa */}
            <div className="mt-3">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                PDF do balancete (opcional)
              </label>
              {formArquivo ? (
                <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <FileText size={15} className="shrink-0 text-brand-600" />
                  <span className="truncate" title={formArquivo.name}>
                    {formArquivo.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormArquivo(null)}
                    className="ml-auto text-slate-400 hover:text-rose-600"
                    aria-label="Remover arquivo"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ) : (
                <DropPdf onArquivo={setFormArquivo} />
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  criarMes.mutate({ ano: formAno, mes: formMes, arquivo: formArquivo })
                }
                disabled={criarMes.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md"
              >
                {criarMes.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CalendarPlus size={14} />
                )}
                {formArquivo ? "Criar e enviar PDF" : "Criar mês"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNovoMesAberto(false);
                  setFormArquivo(null);
                }}
                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800"
              >
                Cancelar
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Anexe o PDF aqui que o sistema salva na pasta certa. Você também pode
              enviá-lo depois, pelo painel do mês.
            </p>
          </div>
        )}

        {lista.length > 0 ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => irPara(-1)}
              disabled={idxAtual <= 0}
              className="shrink-0 p-1.5 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Mês anterior"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex-1 flex gap-2 overflow-x-auto py-1">
              {lista.map((c) => (
                <PilulaMes
                  key={c.id}
                  comp={c}
                  ativo={c.id === selecionadaId}
                  onClick={() => setSelecionadaId(c.id)}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => irPara(1)}
              disabled={idxAtual < 0 || idxAtual >= lista.length - 1}
              className="shrink-0 p-1.5 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Próximo mês"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        ) : null}

        {criarMes.isError && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-start gap-2">
            <FolderOpen size={15} className="mt-0.5 shrink-0 text-amber-700" />
            <span>
              {criarMes.error instanceof ApiError &&
              (criarMes.error.body as { detail?: string })?.detail
                ? (criarMes.error.body as { detail?: string }).detail
                : "Não foi possível criar o mês."}
            </span>
          </div>
        )}

        {/* Painel do mês selecionado */}
        <div className="mt-4">
          {compAtual ? (
            <PainelMes
              comp={compAtual}
              podeGerir={podeGerirBalancete}
              onChange={() => refetchComps()}
              onVerDashboard={() => onVerResultados(compAtual.id)}
              onVerPaginas={() => verPaginasDoMes(compAtual)}
            />
          ) : (
            <div className="p-10 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
              <CalendarPlus size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                Nenhum mês cadastrado ainda.
              </p>
              {podeGerirBalancete && (
                <button
                  type="button"
                  onClick={abrirNovoMes}
                  disabled={novoMesAberto}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md"
                >
                  <CalendarPlus size={16} />
                  Cadastrar primeiro mês
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Pílula de mês (navegador)
// =====================================================================
function PilulaMes({
  comp,
  ativo,
  onClick,
}: {
  comp: CompetenciaResumo;
  ativo: boolean;
  onClick: () => void;
}) {
  const dot = STATUS_DOT[comp.status] ?? STATUS_DOT.pendente;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition ${
        ativo
          ? "bg-brand-700 border-brand-700 text-white shadow-sm"
          : "bg-white border-slate-200 text-slate-700 hover:border-brand-300"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full ${dot} ${ativo ? "ring-2 ring-white/40" : ""}`}
      />
      {MESES_ABREV[comp.mes - 1]}/{comp.ano}
    </button>
  );
}

// =====================================================================
// Área de upload de PDF (arrastar ou clicar) — o backend põe na pasta certa
// =====================================================================
function DropPdf({ onArquivo }: { onArquivo: (f: File) => void }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const escolher = (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      window.alert("Selecione um arquivo PDF.");
      return;
    }
    onArquivo(f);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        escolher(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center text-sm transition ${
        over
          ? "border-brand-500 bg-brand-50 text-brand-700"
          : "border-slate-300 text-slate-500 hover:border-brand-400 hover:text-brand-700"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => escolher(e.target.files)}
      />
      <Upload size={18} className="mx-auto mb-1" />
      Arraste o PDF aqui ou <span className="underline">clique para escolher</span>
    </div>
  );
}

// =====================================================================
// Painel do mês selecionado
// =====================================================================
function PainelMes({
  comp,
  podeGerir,
  onChange,
  onVerDashboard,
  onVerPaginas,
}: {
  comp: CompetenciaResumo;
  podeGerir: boolean;
  onChange: () => void;
  onVerDashboard: () => void;
  onVerPaginas: () => void;
}) {
  const qc = useQueryClient();
  const [erroPasta, setErroPasta] = useState<string | null>(null);
  const [bloqueio, setBloqueio] = useState<string | null>(null);

  const processar = useMutation({
    mutationFn: async () => {
      // Pré-checagem de qualidade do scan (sem custo de OCR). Se o balancete
      // estiver muito torto (além do auto-endireitamento), avisa e deixa o
      // usuário escolher reescanear ou seguir mesmo assim.
      try {
        const q = await api.post<{
          ok: boolean; mensagem: string; pior_angulo: number; n_suspeitas: number;
        }>(`/competencias/${comp.id}/checar-qualidade-scan`);
        if (!q.ok) {
          const seguir = window.confirm(
            `${q.mensagem}\n\n` +
            "OK = processar assim mesmo (a leitura pode sair errada e custar mais)\n" +
            "Cancelar = vou reescanear reto e substituir o PDF antes de processar",
          );
          if (!seguir) throw new Error("__cancelado_qualidade__");
        }
      } catch (e) {
        // Só aborta se foi o usuário que cancelou; falha do pré-check não bloqueia.
        if (e instanceof Error && e.message === "__cancelado_qualidade__") throw e;
      }
      return api.post(`/competencias/${comp.id}/processar-auto`);
    },
    onSuccess: () => {
      setErroPasta(null);
      setBloqueio(null);
      qc.invalidateQueries({ queryKey: ["competencias"] });
      onChange();
    },
    onError: (err) => {
      setErroPasta(null);
      setBloqueio(null);
      if (err instanceof Error && err.message === "__cancelado_qualidade__") {
        return;  // usuário optou por reescanear — silencioso
      }
      if (err instanceof ApiError) {
        const detail = (err.body as { detail?: string } | undefined)?.detail;
        if (err.status === 402) {
          // Orçamento de leitura (OCR) do condomínio atingido.
          setBloqueio(detail ?? "Orçamento mensal de leitura deste condomínio atingido.");
        } else if (err.status === 404) {
          setErroPasta(detail ?? "Não foi possível encontrar o PDF.");
        } else {
          setErroPasta(detail ?? err.message);
        }
      } else {
        setErroPasta((err as Error).message);
      }
    },
  });

  const excluir = useMutation({
    mutationFn: () =>
      api.delete<{ periodo: string; input_dir: string; pdfs: string[] }>(
        `/competencias/${comp.id}`,
      ),
    onSuccess: (info) => {
      setErroPasta(null);
      // Dica após excluir: a exclusão NÃO apaga o PDF físico. Se o usuário vai
      // reprocessar com um PDF novo, precisa remover o antigo desta pasta antes
      // (o app processa TODOS os .pdf da pasta).
      const nomes = info?.pdfs ?? [];
      if (nomes.length > 0) {
        window.alert(
          `Mês ${info.periodo} excluído.\n\n` +
            "ATENÇÃO: o PDF antigo NÃO foi apagado do disco.\n" +
            "Se você vai reprocessar com um PDF NOVO, apague antes o(s) antigo(s) nesta pasta:\n\n" +
            `${info.input_dir}\n\n` +
            `Arquivo(s) a remover: ${nomes.join(", ")}\n\n` +
            'Depois coloque o novo PDF nessa pasta e clique em "Novo mês".',
        );
      } else {
        window.alert(
          `Mês ${info?.periodo ?? ""} excluído.\n\n` +
            `A pasta abaixo está sem PDF — coloque o novo e clique em "Novo mês":\n\n` +
            `${info?.input_dir ?? ""}`,
        );
      }
      qc.invalidateQueries({ queryKey: ["competencias"] });
      onChange();
    },
    // Sem isto, uma falha (ex.: tabela-filha não removida) ficava SILENCIOSA e o
    // usuário via "nada acontece". Agora mostra o motivo.
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Falha ao excluir o mês.";
      setErroPasta(msg);
      window.alert(`Não consegui excluir este mês.\n\n${msg}`);
    },
  });

  const enviarPdf = useMutation({
    mutationFn: (arquivo: File) =>
      api.upload(`/competencias/${comp.id}/upload-pdf`, arquivo),
    onSuccess: () => {
      setErroPasta(null);
      qc.invalidateQueries({ queryKey: ["competencias"] });
      onChange();
    },
    onError: (err) => setErroPasta((err as Error).message),
  });

  const confirmarExcluir = () => {
    const ok = window.confirm(
      `Excluir o balancete de ${MESES[comp.mes - 1]}/${comp.ano}? ` +
        "Esta ação remove os dados deste mês e não pode ser desfeita.",
    );
    if (ok) excluir.mutate();
  };

  // Limpeza SELETIVA do OCR: só deste mês. Força releitura paga no próximo
  // reprocesso (útil quando o PDF foi trocado/reescaneado).
  const limparOcr = useMutation({
    mutationFn: () =>
      api.post<{ removidos: number; n_imagens: number }>(
        `/competencias/${comp.id}/limpar-cache-ocr`,
      ),
    onSuccess: (r) =>
      window.alert(
        `Cache de OCR deste mês limpo (${r.removidos} entradas de ${r.n_imagens} páginas). ` +
          "O próximo Reprocessar deste mês vai reler pelo Vision (custo só dele). " +
          "Os outros meses não foram afetados.",
      ),
  });
  const confirmarLimparOcr = () => {
    const ok = window.confirm(
      `Limpar o OCR de ${MESES[comp.mes - 1]}/${comp.ano} (só deste mês)?\n\n` +
        "Use quando trocar/reescanear o PDF. O próximo Reprocessar vai reler " +
        "pelo Vision (custo real). Os demais meses não são afetados.",
    );
    if (ok) limparOcr.mutate();
  };

  // Reprocessar GERANDO OCR DE NOVO: limpa o cache de OCR deste mês e dispara o
  // reprocessamento em sequência (releitura paga pelo Vision). Avisa da cascata:
  // como os saldos encadeiam mês a mês, meses posteriores já auditados podem
  // precisar ser reprocessados também.
  const reprocessarComOcr = useMutation({
    mutationFn: async () => {
      await api.post(`/competencias/${comp.id}/limpar-cache-ocr`);
      return api.post(`/competencias/${comp.id}/processar-auto`);
    },
    onSuccess: () => {
      setErroPasta(null);
      setBloqueio(null);
      qc.invalidateQueries({ queryKey: ["competencias"] });
      onChange();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = (err.body as { detail?: string } | undefined)?.detail;
        if (err.status === 402) setBloqueio(detail ?? "Orçamento mensal de leitura deste condomínio atingido.");
        else setErroPasta(detail ?? err.message);
      } else {
        setErroPasta((err as Error).message);
      }
    },
  });
  const confirmarReprocessarComOcr = () => {
    const ok = window.confirm(
      `Reprocessar ${MESES[comp.mes - 1]}/${comp.ano} GERANDO OCR de novo?\n\n` +
        "• O balancete deste mês será lido de novo pelo Vision (CUSTO real só deste mês).\n" +
        "• ATENÇÃO — efeito cascata: os saldos encadeiam de um mês para o outro. " +
        "Se já existem meses POSTERIORES auditados, eles podem ficar inconsistentes " +
        "e precisar ser reprocessados também (na ordem cronológica).\n\n" +
        "Deseja continuar?",
    );
    if (ok) reprocessarComOcr.mutate();
  };

  // Limpa erro se o status mudou (ex.: usuário colocou PDF + status atualizou)
  useEffect(() => {
    if (comp.pdfs.length > 0 && erroPasta) setErroPasta(null);
  }, [comp.pdfs.length, erroPasta]);

  const statusCfg = STATUS_VISUAL[comp.status] ?? STATUS_VISUAL.pendente;
  const podeProcessar =
    comp.status !== "processando" && !STATUS_CONCLUIDOS.has(comp.status);
  const concluida = STATUS_CONCLUIDOS.has(comp.status);

  return (
    <div className="overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm">
      {/* Faixa de acento por status */}
      <div className={`h-1.5 ${statusCfg.faixa}`} />

      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Balancete
            </p>
            <h3 className="text-2xl font-bold text-slate-900">
              {MESES[comp.mes - 1]} de {comp.ano}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 font-medium ${statusCfg.classe}`}
            >
              {statusCfg.icone}
              {statusCfg.label}
            </span>
            {podeGerir && (
              <button
                type="button"
                onClick={confirmarExcluir}
                disabled={excluir.isPending || comp.status === "processando"}
                className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 transition"
                title="Excluir este mês"
                aria-label="Excluir este mês"
              >
                {excluir.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            )}
          </div>
        </div>

        {/* PDFs já registrados */}
        {comp.pdfs.length > 0 && (
          <div className="mt-5 space-y-1.5">
            {comp.pdfs.map((pdf) => (
              <div
                key={pdf.id}
                className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2"
              >
                <FileText size={15} className="shrink-0 text-slate-400" />
                <span className="truncate" title={pdf.nome_arquivo}>
                  {pdf.nome_arquivo}
                </span>
                {pdf.n_paginas && (
                  <span className="text-slate-400 shrink-0 ml-auto">
                    {pdf.n_paginas} págs
                  </span>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={onVerPaginas}
              className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:border-brand-400 hover:text-brand-700 text-slate-600 text-xs font-medium rounded-md transition"
              title="Abrir as páginas do balancete deste mês dentro do app (leitura), sem procurar no Windows"
            >
              <BookOpen size={14} />
              Ver páginas do balancete
            </button>
          </div>
        )}

        {/* Barra de progresso (só quando status = processando) */}
        {comp.status === "processando" && <ProgressoEtapas competenciaId={comp.id} />}

        {/* Motivo do erro (status = erro) — mostra o detalhe real, não só "Com problema" */}
        {comp.status === "erro" && comp.observacao && (
          <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-900 flex items-start gap-2">
            <ShieldAlert size={15} className="mt-0.5 shrink-0 text-red-600" />
            <div className="min-w-0">
              <p className="font-medium mb-1">O que deu errado</p>
              <p className="break-words whitespace-pre-wrap">{comp.observacao}</p>
              <p className="mt-2 text-red-800">
                Tente <strong>Reprocessar gerando OCR de novo</strong>. Se repetir, me mande este texto.
              </p>
            </div>
          </div>
        )}

        {/* Bloqueio por orçamento de leitura (OCR) do condomínio */}
        {bloqueio && (
          <div className="mt-5 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-900 flex items-start gap-2">
            <ShieldAlert size={15} className="mt-0.5 shrink-0 text-rose-600" />
            <div>
              <p className="font-medium mb-1">Leitura bloqueada — orçamento do mês atingido</p>
              <p>{bloqueio}</p>
              <p className="mt-2 text-rose-800">
                Ajuste o orçamento em <strong>Uso &amp; Custos</strong> ou aguarde o próximo mês.
              </p>
            </div>
          </div>
        )}

        {/* Mensagem de pasta vazia */}
        {erroPasta && (
          <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-start gap-2">
            <FolderOpen size={15} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <p className="font-medium mb-1">Coloque o PDF do balancete em:</p>
              <code className="block bg-white px-1.5 py-1 rounded border border-amber-200 text-[11px] text-slate-800 break-all">
                {erroPasta.replace(
                  "Não encontrei o PDF do balancete. Coloque o arquivo em: ",
                  "",
                )}
              </code>
              <p className="mt-2 text-amber-800">
                Depois clique em <strong>Processar</strong> de novo.
              </p>
            </div>
          </div>
        )}

        {/* Sem PDF: área de envio (arrastar/escolher) — o sistema põe na pasta certa */}
        {comp.pdfs.length === 0 && comp.status !== "processando" && podeGerir && (
          <div className="mt-5">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
              {enviarPdf.isPending ? (
                <>
                  <Loader2 size={15} className="animate-spin text-brand-600" />
                  Enviando o PDF...
                </>
              ) : (
                <>
                  <Clock size={15} className="text-slate-400" />
                  Envie o PDF do balancete deste mês.
                </>
              )}
            </div>
            {!enviarPdf.isPending && (
              <DropPdf onArquivo={(f) => enviarPdf.mutate(f)} />
            )}
          </div>
        )}

        {/* Ação principal */}
        <div className="mt-6">
          {concluida ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onVerDashboard}
                style={{
                  background: "linear-gradient(135deg,#1fd3c1 0%,#0E8D82 100%)",
                  boxShadow: "0 10px 26px rgba(14,141,130,0.45), 0 0 0 1px rgba(34,201,184,0.25) inset",
                }}
                className="group relative flex-1 inline-flex items-center justify-center gap-2 px-4 py-3.5 text-white text-sm font-semibold rounded-xl transition-all hover:-translate-y-0.5 overflow-hidden"
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-[3px]"
                  style={{ background: "#8ff5ea", boxShadow: "0 0 10px 1px rgba(143,245,234,0.9)" }}
                />
                Ver dashboard do mês
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              {podeGerir && (
                <button
                  type="button"
                  onClick={() => processar.mutate()}
                  disabled={processar.isPending}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-3 border border-slate-300 hover:border-slate-400 text-slate-600 text-sm rounded-lg transition"
                  title="Reprocessar este balancete"
                >
                  {processar.isPending ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <RefreshCw size={15} />
                  )}
                  Reprocessar
                </button>
              )}
            </div>
          ) : podeGerir ? (
            <button
              type="button"
              onClick={() => processar.mutate()}
              disabled={!podeProcessar || processar.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow-sm transition"
            >
              {processar.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Disparando...
                </>
              ) : comp.status === "processando" ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Processar balancete
                </>
              )}
            </button>
          ) : (
            <p className="text-sm text-slate-500 text-center py-2">
              Este mês ainda não foi processado. Aguarde o síndico.
            </p>
          )}

          {/* Reprocessar gerando OCR de novo + limpeza seletiva de OCR */}
          {podeGerir && comp.pdfs.length > 0 && comp.status !== "processando" && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={confirmarReprocessarComOcr}
                disabled={reprocessarComOcr.isPending}
                title="Limpa o OCR deste mês e reprocessa, relendo pelo Vision (custo real só deste mês). Avisa do efeito cascata nos meses posteriores."
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50 text-xs font-medium rounded-md transition"
              >
                {reprocessarComOcr.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <RefreshCw size={13} />
                )}
                Reprocessar gerando OCR de novo
              </button>
              <button
                type="button"
                onClick={confirmarLimparOcr}
                disabled={limparOcr.isPending}
                title="Só limpa o OCR deste mês (sem reprocessar). O próximo Reprocessar relê pelo Vision."
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-amber-700 transition"
              >
                {limparOcr.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Trash2 size={12} />
                )}
                Só limpar OCR
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Configuração visual por status
// =====================================================================
const STATUS_VISUAL: Record<
  string,
  { label: string; classe: string; faixa: string; icone: React.ReactNode }
> = {
  pendente: {
    label: "Aguardando",
    classe: "bg-slate-100 text-slate-700",
    faixa: "bg-slate-300",
    icone: null,
  },
  processando: {
    label: "Processando",
    classe: "bg-blue-100 text-blue-700",
    faixa: "bg-blue-500",
    icone: <Loader2 size={11} className="animate-spin" />,
  },
  auditada: {
    label: "Auditada",
    classe: "bg-emerald-100 text-emerald-700",
    faixa: "bg-emerald-500",
    icone: <CheckCircle2 size={11} />,
  },
  aprovada: {
    label: "Aprovada",
    classe: "bg-emerald-100 text-emerald-700",
    faixa: "bg-emerald-500",
    icone: <CheckCircle2 size={11} />,
  },
  rejeitada: {
    label: "Rejeitada",
    classe: "bg-red-100 text-red-700",
    faixa: "bg-red-500",
    icone: <AlertCircle size={11} />,
  },
  erro: {
    label: "Com problema",
    classe: "bg-red-100 text-red-700",
    faixa: "bg-red-500",
    icone: <AlertCircle size={11} />,
  },
};

const STATUS_DOT: Record<string, string> = {
  pendente: "bg-slate-300",
  processando: "bg-blue-500",
  auditada: "bg-emerald-500",
  aprovada: "bg-emerald-500",
  rejeitada: "bg-red-500",
  erro: "bg-red-500",
};

// =====================================================================
// Barra de progresso visivel quando comp.status === "processando"
// =====================================================================
interface StatusProcessamento {
  competencia_id: number;
  status: string;
  etapa_atual: string | null;
  progresso_pct: number;
  etapas: { etapa: string; status: string; duracao_ms: string }[];
  ocr_atual?: number | null;
  ocr_total?: number | null;
}

// Rotulos em pt-BR (sem jargao tecnico)
const ETAPA_LABEL: Record<string, string> = {
  ingestao: "Lendo PDF",
  ocr: "Reconhecendo texto",
  parse: "Identificando contas e valores",
  persist: "Salvando no banco",
  cotas: "Conferindo cotas",
  chamadas_extras: "Conferindo chamadas extras",
  audit: "Auditando dados",
  finalizando: "Finalizando",
  reports: "Gerando relatórios",
};

function ProgressoEtapas({ competenciaId }: { competenciaId: number }) {
  const { data } = useQuery<StatusProcessamento>({
    queryKey: ["competencia-status", competenciaId],
    queryFn: () =>
      api.get<StatusProcessamento>(`/competencias/${competenciaId}/status`),
    refetchInterval: 1500,
  });

  if (!data) {
    return (
      <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 size={14} className="animate-spin" />
        Iniciando...
      </div>
    );
  }

  let etapaAtual =
    data.etapa_atual && ETAPA_LABEL[data.etapa_atual]
      ? ETAPA_LABEL[data.etapa_atual]
      : "Processando";
  // Durante o OCR, mostra o progresso página a página.
  if (data.etapa_atual === "ocr" && data.ocr_total) {
    etapaAtual = `Reconhecendo texto — página ${data.ocr_atual ?? 0} de ${data.ocr_total}`;
  }
  // Trava defensiva: a barra nunca passa de 100% nem fica negativa.
  const pct = Math.max(0, Math.min(100, Math.round(data.progresso_pct)));

  return (
    <div className="mt-5 space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700 flex items-center gap-1.5">
          <Loader2 size={13} className="animate-spin text-brand-700" />
          {etapaAtual}...
        </span>
        <span className="text-slate-500 tabular-nums">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-700 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-1 pt-1">
        {data.etapas.map((e) => (
          <span
            key={e.etapa}
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              e.status === "completed"
                ? "bg-emerald-50 text-emerald-700"
                : e.status === "started" || e.status === "in_progress"
                ? "bg-blue-50 text-blue-700"
                : e.status === "failed"
                ? "bg-rose-50 text-rose-700"
                : "bg-slate-50 text-slate-400"
            }`}
            title={ETAPA_LABEL[e.etapa] ?? e.etapa}
          >
            {e.etapa}
            {e.status === "completed" && " ✓"}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Logo (opcional) do condomínio — mostra num círculo no topo do painel. O síndico/Dono
 * troca ou remove; o Portal do Condômino lê a mesma imagem. JPG/PNG do desktop.
 */
function LogoCondominio({ condominioId, podeEditar }: { condominioId: number; podeEditar: boolean }) {
  const qc = useQueryClient();
  const ref = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data } = useQuery<{ logo: string | null }>({
    queryKey: ["logo-condominio", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/logo`),
    retry: false,
  });
  const logo = data?.logo ?? null;
  const inval = () => qc.invalidateQueries({ queryKey: ["logo-condominio", condominioId] });

  const enviar = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("arquivo", file);
      return api.putForm(`/condominios/${condominioId}/logo`, fd);
    },
    onSuccess: () => { setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível enviar o logo."),
  });
  const remover = useMutation({
    mutationFn: () => api.delete(`/condominios/${condominioId}/logo`),
    onSuccess: inval,
  });

  return (
    <div className="flex items-center gap-3 mb-2">
      {logo ? (
        <img src={logo} alt="Logo do condomínio"
          className="w-14 h-14 rounded-full object-cover ring-2 ring-white/60 flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-white/15 text-white/80 flex items-center justify-center flex-shrink-0">
          <Building2 size={26} />
        </div>
      )}
      {podeEditar && (
        <div className="flex items-center gap-1.5">
          <input ref={ref} type="file" accept="image/jpeg,image/png" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) enviar.mutate(f); e.currentTarget.value = ""; }} />
          <button type="button" onClick={() => ref.current?.click()} disabled={enviar.isPending}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white disabled:opacity-50"
            title="Enviar um logo (JPG ou PNG)">
            {enviar.isPending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {logo ? "Trocar logo" : "Adicionar logo"}
          </button>
          {logo && (
            <button type="button" onClick={() => { if (confirm("Remover o logo?")) remover.mutate(); }}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white"
              title="Remover o logo"><Trash2 size={12} /></button>
          )}
        </div>
      )}
      {erro && <span className="text-[11px] text-amber-200">{erro}</span>}
    </div>
  );
}
